import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Auth, auth } from "../lib/api.js";
import { TextField, Button, Banner, normalizePhone, isValidEmail, isValidPhone, COLORS as C } from "../components/UI.jsx";

export default function Signup() {
  const navigate = useNavigate();
  if (auth.loggedIn()) { navigate("/dashboard", { replace: true }); return null; }

  const [form, setForm] = useState({
    business_name: "", full_name: "", email: "", phone: "",
    password: "", confirm: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const upd = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (fieldErrors[k]) setFieldErrors((fe) => ({ ...fe, [k]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!form.business_name.trim()) errs.business_name = "Required";
    if (!form.full_name.trim()) errs.full_name = "Required";
    if (!form.email.trim()) errs.email = "Required";
    else if (!isValidEmail(form.email)) errs.email = "Enter a valid email address";
    if (!form.phone.trim()) errs.phone = "Required";
    else if (!isValidPhone(form.phone)) errs.phone = "Enter a 10-digit Indian mobile number";
    if (!form.password) errs.password = "Required";
    else if (form.password.length < 8) errs.password = "At least 8 characters";
    else if (!/[A-Z]/.test(form.password)) errs.password = "Must contain an uppercase letter";
    else if (!/[a-z]/.test(form.password)) errs.password = "Must contain a lowercase letter";
    else if (!/\d/.test(form.password)) errs.password = "Must contain a number";
    if (form.password && form.password !== form.confirm) errs.confirm = "Passwords don't match";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async () => {
    setError(null);
    if (!validate()) return;
    setBusy(true);
    try {
      const phone = normalizePhone(form.phone);
      await Auth.register({
        business_name: form.business_name.trim(),
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone,
        password: form.password,
        account_type: "merchant",
        accept_terms: true,
        accept_privacy: true,
        data_processing_consent: true,
      });
      try {
        await Auth.login(form.email.trim().toLowerCase(), form.password);
        navigate("/dashboard");
      } catch {
        navigate("/login");
      }
    } catch (err) {
      const msg = err.message || "";
      if (msg.toLowerCase().includes("email")) {
        setFieldErrors((fe) => ({ ...fe, email: msg }));
      } else if (msg.toLowerCase().includes("phone")) {
        setFieldErrors((fe) => ({ ...fe, phone: msg }));
      } else {
        setError(msg || "Could not create account. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: C.cream,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, fontFamily: "-apple-system, system-ui, Inter, sans-serif",
    }}>
      <div style={{ width: "100%", maxWidth: 460 }}>
        <Link to="/" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
          <div style={{
            width: 32, height: 32, background: C.red, borderRadius: 8,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: C.white, fontWeight: 800, fontSize: 16,
          }}>S</div>
          <div style={{ fontSize: 19, fontWeight: 800, color: C.navy }}>Shiftswift</div>
        </Link>

        <div style={{
          background: C.white, borderRadius: 14, padding: 32,
          border: `1px solid ${C.line}`,
          boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
        }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: C.navy, margin: 0 }}>
            Open your account
          </h1>
          <p style={{ fontSize: 14, color: C.text3, marginTop: 8, marginBottom: 24 }}>
            Sets up in 60 seconds. No credit card needed.
          </p>

          {error && <Banner kind="error" onDismiss={() => setError(null)}>{error}</Banner>}

          <TextField
            label="Business name" value={form.business_name} onChange={upd("business_name")}
            error={fieldErrors.business_name} required autoFocus
            placeholder="Sharma Trading Co."
            autoComplete="organization"
          />
          <TextField
            label="Your name" value={form.full_name} onChange={upd("full_name")}
            error={fieldErrors.full_name} required
            placeholder="Priya Sharma"
            autoComplete="name"
          />
          <TextField
            label="Email" type="email" value={form.email} onChange={upd("email")}
            error={fieldErrors.email} required
            placeholder="priya@business.com"
            autoComplete="email"
          />
          <TextField
            label="Phone" type="tel" value={form.phone} onChange={upd("phone")}
            error={fieldErrors.phone} required format="phone"
            placeholder="98765 43210"
            autoComplete="tel"
            hint="Indian mobile number — we'll send a verification code here"
          />
          <TextField
            label="Password" type="password" value={form.password} onChange={upd("password")}
            error={fieldErrors.password} required
            placeholder="At least 8 characters"
            autoComplete="new-password"
            hint={!fieldErrors.password ? "Min 8 chars, 1 uppercase, 1 lowercase, 1 number" : null}
          />
          <TextField
            label="Confirm password" type="password" value={form.confirm} onChange={upd("confirm")}
            error={fieldErrors.confirm} required
            placeholder="Repeat your password"
            autoComplete="new-password"
            onEnter={submit}
          />

          <Button onClick={submit} busy={busy}>
            {busy ? "Creating your account…" : "Create account"}
          </Button>

          <p style={{ fontSize: 11, color: C.text4, textAlign: "center", marginTop: 12, lineHeight: 1.5 }}>
            By creating an account you agree to our Terms and Privacy Policy.
          </p>

          <div style={{
            marginTop: 16, paddingTop: 20, borderTop: `1px solid ${C.line}`,
            fontSize: 14, color: C.text2, textAlign: "center",
          }}>
            Already have an account?{" "}
            <Link to="/login" style={{ color: C.red, fontWeight: 700, textDecoration: "none" }}>
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
